/**
 * ShareCard.jsx — Phase 10 Shareability
 *
 * Hidden 1080×1080 DOM node that html2canvas rasterises to PNG. Rendered
 * off-screen (position absolute, top -10000px) so the user never sees it
 * before the capture. The viewport is forced to 1080px by inline styles —
 * Tailwind classes are intentionally avoided inside the capture area so
 * html2canvas doesn't have to resolve runtime CSS variables.
 *
 * Phase 10: variable-length days (0..50 logros). Grid adapts:
 *   - N <= 9   → 3x3 (unchanged from Phase 9)
 *   - N == 10..12 → 3 cols × 4 rows
 *   - N > 12   → first 12 shown in 3×4, "+X más" pill at the bottom
 *
 * Props
 * -----
 *   - logros:     [{ text, completed }]
 *   - perfectToday: boolean
 *   - streak:     number
 *   - streakBest: number
 *   - totalPerfect: number
 *   - date:       Date | ISO string
 *   - locale:     i18n language code (fallback "es")
 */
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

const LOCALE_MAP = { es: "es-ES", en: "en-US", pt: "pt-BR", hi: "hi-IN" };

const CELL_GRID_GAP_PX = 18;
const MAX_CELLS = 12;
const MAX_CHARS_PER_LINE = 26;

function truncate(text, max = MAX_CHARS_PER_LINE) {
  if (!text) return "";
  const clean = String(text).trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1) + "…";
}

function formatLongDate(value, lng) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    return d.toLocaleDateString(LOCALE_MAP[lng] || "es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Given the total number of logros the user has today, decide the grid
 * shape (columns × rows) and how many cells to render. Overflow past 12
 * is truncated and surfaced as a "+X más" pill below the grid.
 */
function computeGridShape(total) {
  if (total <= 9) {
    return { cols: 3, rows: 3, cells: Math.max(total, 9), overflow: 0 };
  }
  if (total <= 12) {
    return { cols: 3, rows: 4, cells: total, overflow: 0 };
  }
  return { cols: 3, rows: 4, cells: MAX_CELLS, overflow: total - MAX_CELLS };
}

const ShareCard = forwardRef(function ShareCard(
  { logros = [], streak = 0, streakBest = 0, totalPerfect = 0, date = new Date() },
  ref,
) {
  const { t, i18n } = useTranslation();
  const list = Array.isArray(logros) ? logros : [];
  const total = list.length;
  const { cols, rows, cells, overflow } = computeGridShape(total);
  const rendered = list.slice(0, cells);
  // For <=9 legacy branding parity: pad up to 9 so the 3×3 grid never has
  // empty cells (product decision — the shareable card must always feel
  // "full" when the day has ≤9 items).
  while (rendered.length < cells) rendered.push({ text: "", completed: true });

  const heroTitle = total > 0
    ? t("share.card.hero_title_dynamic", { n: total })
    : t("share.card.hero_title");

  // Slightly shrink cell padding + iconography above 9 items so a 3×4
  // grid still fits inside the 1080px canvas without clipping the stats
  // row. Numbers were dialled in by hand.
  const isDense = total > 9;
  const cellPadding = isDense ? "12px 14px" : "18px 20px";
  const iconSize = isDense ? 36 : 44;
  const cellRadius = isDense ? "14px" : "18px";
  const cellIndexFont = isDense ? "12px" : "13px";
  const cellTextFont = isDense ? "15px" : "17px";

  return (
    <div
      ref={ref}
      data-testid="share-card"
      style={{
        position: "fixed",
        top: "-10000px",
        left: "-10000px",
        width: "1080px",
        height: "1080px",
        background:
          "radial-gradient(80% 80% at 50% 0%, #0D1530 0%, #0A0E1A 60%, #060912 100%)",
        color: "#E5E7EB",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        overflow: "hidden",
        padding: "72px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
              fontSize: "30px",
              letterSpacing: "0.18em",
              color: "#00E5FF",
              textShadow: "0 0 18px rgba(0,229,255,0.45)",
              fontWeight: 700,
            }}
          >
            NexAgenda Y
          </div>
          <div style={{ marginTop: "8px", fontSize: "18px", color: "#9CA3AF" }}>
            {t("share.card.tagline")}
          </div>
        </div>
        <div
          style={{
            padding: "10px 18px",
            borderRadius: "999px",
            border: "1px solid rgba(0,229,255,0.35)",
            background: "rgba(0,229,255,0.08)",
            color: "#00E5FF",
            fontSize: "16px",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {t("share.card.hero_eyebrow")}
        </div>
      </div>

      {/* Hero */}
      <div style={{ textAlign: "center", marginTop: "-16px" }}>
        <div
          data-testid="share-card-hero"
          style={{
            fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
            fontSize: isDense ? "76px" : "88px",
            fontWeight: 800,
            color: "#00E5FF",
            textShadow: "0 0 35px rgba(0,229,255,0.55)",
            lineHeight: 1,
          }}
        >
          {heroTitle}
        </div>
        <div
          style={{
            marginTop: "12px",
            fontSize: "22px",
            color: "#E5E7EB",
            letterSpacing: "0.02em",
          }}
        >
          {t("share.card.hero_subtitle")}
        </div>
      </div>

      {/* Dynamic grid (3×3 / 3×4) */}
      <div
        data-testid="share-card-grid"
        data-cols={cols}
        data-rows={rows}
        data-cells={cells}
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: `${CELL_GRID_GAP_PX}px`,
          marginTop: "20px",
        }}
      >
        {rendered.map((item, idx) => {
          const label = truncate(item?.text) || `#${idx + 1}`;
          return (
            <div
              key={idx}
              style={{
                borderRadius: cellRadius,
                border: "1px solid rgba(0,229,255,0.18)",
                background: "rgba(255,255,255,0.03)",
                padding: cellPadding,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              {/* Cyan check disc */}
              <div
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  borderRadius: "999px",
                  background: "rgba(0,229,255,0.22)",
                  border: "1.5px solid #00E5FF",
                  boxShadow: "0 0 14px rgba(0,229,255,0.55)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#0A0E1A",
                  fontWeight: 900,
                  fontSize: isDense ? "20px" : "24px",
                  lineHeight: 1,
                }}
              >
                ✓
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
                    fontSize: cellIndexFont,
                    color: "#00E5FF",
                    letterSpacing: "0.12em",
                  }}
                >
                  #{(idx + 1).toString().padStart(2, "0")}
                </div>
                <div
                  style={{
                    marginTop: "2px",
                    fontSize: cellTextFont,
                    lineHeight: 1.2,
                    color: "#E5E7EB",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overflow chip (>12 items) */}
      {overflow > 0 ? (
        <div
          data-testid="share-card-overflow"
          style={{
            marginTop: "10px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              padding: "8px 18px",
              borderRadius: "999px",
              border: "1px solid rgba(0,229,255,0.35)",
              background: "rgba(0,229,255,0.08)",
              color: "#00E5FF",
              fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
              fontSize: "16px",
              letterSpacing: "0.08em",
            }}
          >
            {t("share.card.overflow_more", { count: overflow })}
          </span>
        </div>
      ) : null}

      {/* Stats row */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          justifyContent: "space-between",
          marginTop: "16px",
        }}
      >
        <StatCell
          label={t("share.card.streak_label")}
          value={String(streak)}
        />
        <StatCell
          label={t("share.card.record_label")}
          value={`${streakBest}`}
        />
        <StatCell
          label={t("share.card.total_label")}
          value={`${totalPerfect}`}
        />
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: "1px solid rgba(0,229,255,0.12)",
          paddingTop: "18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ fontSize: "15px", color: "#9CA3AF" }}>
          {formatLongDate(date, i18n.language)}
        </div>
        <div
          style={{
            fontSize: "14px",
            color: "#9CA3AF",
            letterSpacing: "0.04em",
          }}
        >
          {t("share.card.footer")}
        </div>
      </div>
    </div>
  );
});

function StatCell({ label, value }) {
  return (
    <div
      style={{
        flex: 1,
        borderRadius: "16px",
        border: "1px solid rgba(0,229,255,0.14)",
        background: "rgba(255,255,255,0.03)",
        padding: "16px 22px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontFamily: "'JetBrains Mono', 'SF Mono', monospace",
          fontSize: "38px",
          fontWeight: 800,
          color: "#00E5FF",
          textShadow: "0 0 18px rgba(0,229,255,0.4)",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: "6px",
          fontSize: "14px",
          color: "#9CA3AF",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default ShareCard;
